import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Crown, Sparkles, BookOpen, Users, Zap } from 'lucide-react';
export function PSIGatheringSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <section className="relative py-12 sm:py-16 md:py-20 px-5 sm:px-6 bg-gradient-to-b from-black via-purple-950/20 to-black overflow-hidden" data-id="element-2478" data-mp="7wixw9nfyrer">
      {/* Mystical background effects */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                         radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-2479" data-mp="10kx0l2ph6bwn" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/3 w-64 h-64 sm:w-96 sm:h-96 bg-purple-600/10 rounded-full blur-2xl sm:blur-3xl animate-pulse-slow" data-id="element-2480" data-mp="1oarb3dmbnodq" />
      <div className="absolute bottom-1/4 right-1/3 w-64 h-64 sm:w-96 sm:h-96 bg-blue-600/10 rounded-full blur-2xl sm:blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-2481" data-mp="1ji7x4fjq2r8e" />

      <motion.div ref={ref} initial={{
      opacity: 0,
      y: 30
    }} animate={inView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.8
    }} className="relative z-10 max-w-4xl mx-auto" data-id="element-2482" data-mp="w1zalg4py1kk">
        {/* Decorative header */}
        <div className="flex items-center justify-center gap-4 mb-8 sm:mb-12" data-id="element-2483" data-mp="8cd9uo8p7eag">
          <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-purple-500/50" data-id="element-2484" data-mp="dbzpt5fwv8my" />
          <Crown className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500" data-id="element-2485" data-mp="3vi48p2ea2l8s" />
          <div className="w-16 sm:w-24 h-px bg-gradient-to-l from-transparent via-purple-500/50 to-purple-500/50" data-id="element-2486" data-mp="3vjyv6l576lez" />
        </div>

        {/* Main headline */}
        <h2 className="text-3xl sm:text-4xl md:text-6xl font-serif text-center mb-8 sm:mb-12 leading-tight text-stone-100" style={{
        fontFamily: 'Cinzel, serif',
        textShadow: '0 0 30px rgba(168, 85, 247, 0.4)'
      }} data-id="element-2487" data-mp="1trkh5x5ry13u">
          At the{' '}
          <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent" style={{
          textShadow: '0 0 40px rgba(168, 85, 247, 0.6)'
        }} data-id="element-2488" data-mp="31pya0rauae7w">
            Peptide Science Institute
          </span>
          , We Are Doing What the Scholars at the Great Library of Alexandria
          Did:
        </h2>

        {/* Body content */}
        <div className="space-y-4 sm:space-y-6 text-base sm:text-lg md:text-xl leading-relaxed text-stone-300" data-id="element-2489" data-mp="3ph3sle85zklw">
          <p className="text-xl sm:text-2xl font-bold text-center text-purple-400" style={{
          textShadow: '0 0 30px rgba(168, 85, 247, 0.4)'
        }} data-id="element-2490" data-mp="3gmpbr77zk20o">
            We are gathering the most vital, accurate, and powerful information
            about human biology and bringing it together.
          </p>

          <div className="bg-gradient-to-br from-purple-950/30 to-black border-l-4 border-purple-500 p-6 sm:p-8 my-6 sm:my-8" data-id="element-2491" data-mp="86m3p0cyobbt">
            <p className="text-stone-100 mb-4 text-base sm:text-lg" data-id="element-2492" data-mp="fjlm4nmuiohn">
              Our team, spearheaded by{' '}
              <span className="font-bold text-purple-400" data-id="element-2493" data-mp="khn75e3u0xvr">Brenden Henry</span>{' '}
              and{' '}
              <span className="font-bold text-purple-400" data-id="element-2494" data-mp="pf6myig6hipt">Dr. Ali Mazloum</span>
              , has been obsessively discovering, collecting and refining this
              "scattered" wisdom.
            </p>
          </div>

          {/* Brenden's achievements */}
          <div className="space-y-4 pl-4 sm:pl-6 border-l-2 border-cyan-500/30 text-base sm:text-lg" data-id="element-2495" data-mp="13f7lvscvug2n">
            <p className="text-cyan-400 font-bold" data-id="element-2496" data-mp="1880s6ko01wgu">
              Brenden discovered and translated many of the original Russian
              peptide research papers and brought them to the West for the first
              time in history.
            </p>
            <p data-id="element-2497" data-mp="1m6qohy2zvs45">
              He has gone deeper into the workings of peptides in the human body
              than perhaps anyone else alive.
            </p>
          </div>

          {/* Team achievements */}
          <p className="text-base sm:text-lg" data-id="element-2498" data-mp="14iokozuaf7n1">
            And together with Dr. Ali and our team here at PSI, has spearheaded
            new, original research which unveiled the existence of{' '}
            <span className="font-bold text-purple-400" data-id="element-2499" data-mp="1db03bzj5tqsg">
              THREE types of bioregulator peptides.
            </span>
          </p>

          <p className="text-stone-400 italic text-center text-sm sm:text-base" data-id="element-2500" data-mp="plnzlf6lqaxj">
            (Along with a class of "super peptides" we've categorized as
            bioaugmentors.)
          </p>

          {/* Key value proposition */}
          <div className="bg-gradient-to-br from-purple-950/40 via-blue-950/30 to-black border-2 border-purple-500/50 p-6 sm:p-8 md:p-10 my-8 sm:my-12 relative" data-id="element-2501" data-mp="uk8pako89c7q">
            <div className="absolute top-0 left-0 w-8 sm:w-10 h-8 sm:h-10 border-l-2 border-t-2 border-purple-500" data-id="element-2502" data-mp="2awxkntlszq2t" />
            <div className="absolute top-0 right-0 w-8 sm:w-10 h-8 sm:h-10 border-r-2 border-t-2 border-blue-500" data-id="element-2503" data-mp="261i8d63g228i" />
            <div className="absolute bottom-0 left-0 w-8 sm:w-10 h-8 sm:h-10 border-l-2 border-b-2 border-blue-500" data-id="element-2504" data-mp="3nzdeb2q3bvik" />
            <div className="absolute bottom-0 right-0 w-8 sm:w-10 h-8 sm:h-10 border-r-2 border-b-2 border-purple-500" data-id="element-2505" data-mp="3ewm5zbsihfv5" />

            <p className="text-lg sm:text-xl md:text-2xl font-bold text-center text-stone-100 leading-relaxed" data-id="element-2506" data-mp="3jwgtd5d2vnbg">
              With our combined knowledge of peptides and the human body, our
              team has largely taken the{' '}
              <span className="text-purple-400" data-id="element-2507" data-mp="fgfsfn6k5i99">
                guesswork out of biological regeneration, enhancement and
                optimization.
              </span>
            </p>
          </div>

          {/* Three key points */}
          <div className="space-y-4 sm:space-y-6 my-8 sm:my-12" data-id="element-2508" data-mp="udybc8wk118s">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="flex items-start gap-4 p-4 sm:p-6 bg-gradient-to-r from-purple-950/30 to-transparent border-l-4 border-purple-500 rounded-r-lg" data-id="element-2509" data-mp="ucevgjet8s7j">
              <Sparkles className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" data-id="element-2510" data-mp="z4tzuvsqjszi" />
              <p className="text-stone-100 text-base sm:text-lg" data-id="element-2511" data-mp="u3o6cvr941ox">
                We've created a{' '}
                <span className="font-bold text-purple-400" data-id="element-2512" data-mp="q3nh0g2m6633">
                  CENTRALIZED source of truth.
                </span>
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="flex items-start gap-4 p-4 sm:p-6 bg-gradient-to-r from-blue-950/30 to-transparent border-l-4 border-blue-500 rounded-r-lg" data-id="element-2513" data-mp="gdayeted0mzl">
              <Zap className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" data-id="element-2514" data-mp="2dztyna4tjnyf" />
              <p className="text-stone-100 text-base sm:text-lg" data-id="element-2515" data-mp="2i0nhhp3emckg">
                A{' '}
                <span className="font-bold text-blue-400" data-id="element-2516" data-mp="2n1tfa1bx8q0u">
                  command center for biological mastery.
                </span>
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="flex items-start gap-4 p-4 sm:p-6 bg-gradient-to-r from-purple-950/30 to-transparent border-l-4 border-purple-500 rounded-r-lg" data-id="element-2517" data-mp="1ucmh53gdq2g4">
              <BookOpen className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" data-id="element-2518" data-mp="1bkl8589w4vsz" />
              <p className="text-stone-100 text-base sm:text-lg" data-id="element-2519" data-mp="1gjxwxykbocy7">
                A place where the scattered knowledge has been{' '}
                <span className="font-bold text-purple-400" data-id="element-2520" data-mp="9xx4ihbp9m94">
                  gathered, organized, validated, and made accessible.
                </span>
              </p>
            </motion.div>
          </div>

          {/* Peptide Mastery reveal */}
          <p className="text-lg sm:text-xl font-bold text-stone-100 text-center" data-id="element-2521" data-mp="dyh3zrjqx1yf">
            And we've distilled much of this knowledge into the most
            comprehensive, scientifically validated, and in-depth education on
            peptides ever created:
          </p>

          <div className="bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-600 p-6 sm:p-8 md:p-10 my-6 sm:my-8 relative" data-id="element-2522" data-mp="11ojs5wkjhnrr">
            <div className="absolute top-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-l-2 border-t-2 border-amber-600" data-id="element-2523" data-mp="xo0zk0atodoi" />
            <div className="absolute top-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-r-2 border-t-2 border-amber-600" data-id="element-2524" data-mp="1bnip4hvxzuyx" />
            <div className="absolute bottom-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-l-2 border-b-2 border-amber-600" data-id="element-2525" data-mp="16ph7m1e603pk" />
            <div className="absolute bottom-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-r-2 border-b-2 border-amber-600" data-id="element-2526" data-mp="1q7lj0pmv62pq" />

            <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-amber-500 mb-4" style={{
            fontFamily: 'Cinzel, serif',
            textShadow: '0 0 40px rgba(217, 119, 6, 0.6)'
          }} data-id="element-2527" data-mp="1gbigvjs0i42s">
              Peptide Mastery 2.0
            </p>

            <p className="text-lg sm:text-xl text-center text-stone-100" data-id="element-2528" data-mp="17gvih7krms01">
              Which is now being included{' '}
              <span className="font-bold text-green-400" data-id="element-2529" data-mp="xl0r9o2h7oln">FREE</span> when you
              join{' '}
              <span className="font-bold text-purple-400" data-id="element-2530" data-mp="12jlrvoln4aam">
                PSI PRO — The Private Research Order.
              </span>
            </p>
          </div>

          {/* Closing emphasis */}
          <div className="text-center space-y-4 py-6 sm:py-8" data-id="element-2531" data-mp="slfrjbfhfc7v"></div>
        </div>
      </motion.div>
    </section>;
}